import { Component, EventEmitter, Input, Output, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { CellularBillingService } from 'src/app/shared/services/cellular-billing.service';
import { BillingActivePartner, BillingOverageRate } from 'src/app/shared/model/cellular-billing';
import { MatDialog } from '@angular/material/dialog';
import { ConfigurationDeleteDialogComponent } from '../dialogs/configuration-delete-dialog.component';

@Component({ selector: 'app-billing-pricing', templateUrl: './billing-pricing.component.html', styleUrls: ['./billing-pricing.component.scss'] })
export class BillingPricingComponent implements OnInit, OnDestroy {
  @Input() section: 'all' | 'overages' | 'partners' = 'all';
  @Input() embedded = false;
  @Input() disabled = false;
  @Output() changed = new EventEmitter<void>();
  readonly tiers = ['LIST', 'REFERRAL', 'AUTHORIZED', 'GOLD', 'PLATINUM', 'DISTRIBUTOR', 'SILVER'];
  rates: BillingOverageRate[] = [];
  partners: BillingActivePartner[] = [];
  rateSearch = ''; partnerSearch = '';
  loading = false; saving = false; addingRate = false; addingPartner = false;
  error = ''; success = '';
  editingRate: BillingOverageRate;
  editingPartner: BillingActivePartner;
  readonly rateForm = this.fb.group({ code: ['', Validators.required], label: ['', Validators.required], bundleGb: [1, [Validators.required, Validators.min(0.001)]],
    LIST: [null], REFERRAL: [null], AUTHORIZED: [null], GOLD: [null], PLATINUM: [null], DISTRIBUTOR: [null], SILVER: [null] });
  readonly partnerForm = this.fb.group({ partnerName: ['', Validators.required], sourceTier: ['', Validators.required] });
  private readonly subscriptions = new Subscription();
  constructor(private fb: FormBuilder, private billing: CellularBillingService, private dialog: MatDialog) {}
  ngOnInit(): void { this.load(); this.subscriptions.add(this.billing.configurationChanged.subscribe(() => this.load())); }
  ngOnDestroy(): void { this.subscriptions.unsubscribe(); }
  load(): void {
    this.loading = true; this.error = '';
    this.subscriptions.add(forkJoin([this.billing.listDataOverages(), this.billing.listActivePartners()]).pipe(finalize(() => this.loading = false))
      .subscribe(([rates, partners]) => { this.rates = rates; this.partners = partners; }, () => this.error = 'Unable to load billing configuration. Retry.'));
  }
  get visibleRates(): BillingOverageRate[] { const term = this.rateSearch.trim().toLowerCase(); return this.rates.filter(row => (row.code + ' ' + row.label).toLowerCase().includes(term)); }
  get visiblePartners(): BillingActivePartner[] { const term = this.partnerSearch.trim().toLowerCase(); return this.partners.filter(row => (row.partnerName + ' ' + row.sourceTier + ' ' + (row.tier || 'Unresolved')).toLowerCase().includes(term)); }
  saveRate(): void {
    if (this.rateForm.invalid || this.saving || this.disabled) return;
    const value = this.rateForm.value;
    const prices = this.tiers.reduce((result, tier) => ({ ...result, [tier]: value[tier] }), {});
    const input = { code: value.code.trim().toUpperCase(), label: value.label, bundleGb: value.bundleGb, prices };
    this.perform(this.editingRate ? this.billing.updateConfiguration('data-overages', this.editingRate._id, this.editingRate.revision || 0, input) : this.billing.createDataOverage(input));
  }
  savePartner(): void {
    if (this.partnerForm.invalid || this.saving || this.disabled) return;
    this.perform(this.editingPartner ? this.billing.updateConfiguration('active-partners', this.editingPartner._id, this.editingPartner.revision || 0, this.partnerForm.value) : this.billing.createActivePartner(this.partnerForm.value));
  }
  openRateForm(): void { this.editingRate = undefined; this.rateForm.reset({ bundleGb: 1 }); this.addingRate = !this.addingRate; }
  openPartnerForm(): void { this.editingPartner = undefined; this.partnerForm.reset(); this.addingPartner = !this.addingPartner; }
  editRate(row: BillingOverageRate): void {
    if (this.saving || this.disabled) return;
    this.editingRate = row; this.addingRate = true;
    this.rateForm.reset({ code: row.code, label: row.label, bundleGb: row.bundleGb,
      ...this.tiers.reduce((result, tier) => ({ ...result, [tier]: row.pricesCents[tier] === undefined ? null : row.pricesCents[tier] / 100 }), {}) });
  }
  editPartner(row: BillingActivePartner): void {
    if (this.saving || this.disabled) return;
    this.editingPartner = row; this.addingPartner = true;
    this.partnerForm.reset({ partnerName: row.partnerName, sourceTier: row.tier || '' });
  }
  deleteRecord(kind: 'data-overages' | 'active-partners', row: BillingOverageRate | BillingActivePartner): void {
    if (this.saving || this.disabled) return;
    const label = kind === 'data-overages' ? (row as BillingOverageRate).code : (row as BillingActivePartner).partnerName;
    const ref = this.dialog.open(ConfigurationDeleteDialogComponent, { width: '480px', maxWidth: 'calc(100vw - 32px)', data: { kind: kind === 'data-overages' ? 'overage product' : 'active partner', label } });
    this.subscriptions.add(ref.afterClosed().subscribe(confirmed => {
      if (confirmed) this.perform(this.billing.deleteConfiguration(kind, row._id, row.revision || 0), 'Record deleted from active configuration. History retained.');
    }));
  }
  importPriceCard(): void { this.perform(this.billing.importSuppliedPriceCard()); }
  importPartners(event: Event): void {
    const input = event.target as HTMLInputElement; const file = input.files && input.files[0]; input.value = '';
    if (!file || this.saving || this.disabled) return;
    if (!/\.xlsx$/i.test(file.name) || file.size > 20 * 1024 * 1024) { this.error = 'Choose an XLSX file up to 20 MB.'; return; }
    this.perform(this.billing.importActivePartners(file));
  }
  private perform(operation: Observable<any>, message?: string): void {
    if (this.saving || this.disabled) return;
    this.saving = true; this.error = ''; this.success = '';
    this.subscriptions.add(operation.pipe(finalize(() => this.saving = false)).subscribe(response => {
      this.success = message || (response.data.inserted === undefined ? 'Changes saved. Current draft pricing refreshed.' : `${response.data.inserted} new records saved; ${response.data.skipped} existing records left unchanged.`);
      this.addingRate = false; this.addingPartner = false;
      this.editingRate = undefined; this.editingPartner = undefined;
      this.billing.configurationChanged.next();
      if (response.data.inserted === undefined || response.data.inserted > 0) this.changed.emit();
    }, error => this.error = error && error.error && error.error.error && error.error.error.message || 'Unable to save configuration.'));
  }
}
